import { useSearchParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { useCatalog, useCatalogGroups } from "../catalog.ts";
import { useDownloadStats } from "../downloads.ts";
import { errorCopyKey } from "../errors.ts";
import { FitFinder, FitResults } from "./FitFinder.tsx";
import { HowItWorks } from "./HowItWorks.tsx";
import { PageHead } from "./PageHead.tsx";
import { DefaultCatalogCard, DefaultEmptyState } from "./defaults.tsx";

export function CatalogPage() {
  const { brand, copy, api } = useApp();
  const Card = brand.slots?.CatalogCard ?? DefaultCatalogCard;
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const q = useCatalog();
  const downloads = useDownloadStats();
  const groups = useCatalogGroups(q.data);
  const [search, setSearch] = useSearchParams();
  // The set search needs community tags to look through.
  const finder = !!api && brand.config.features.fitTags && brand.config.features.publicGallery;
  const fit = (finder && search.get("fit")) || undefined;

  return (
    <section className="page page-catalog">
      {finder ? (
        // The set search is the hero: find brackets tagged for your set, or pick one below and size it yourself.
        <PageHead
          className="home-hero"
          eyebrow={copy("catalog.eyebrow")}
          title={
            <>
              {copy("home.findLabel")} <a className="home-hero-or" href="#catalog-all">{copy("home.orCustomize")}</a>
            </>
          }
        >
          <FitFinder value={fit} onChange={(ref) => setSearch(ref ? { fit: ref } : {})} />
          {/* How it works, until a set is picked (then its results take the space). */}
          {!fit && <HowItWorks />}
        </PageHead>
      ) : (
        <PageHead eyebrow={copy("catalog.eyebrow")} title={copy("catalog.title")} sub={brand.config.tagline || undefined} />
      )}
      {fit && q.data && <FitResults fit={fit} catalog={q.data} />}
      {q.isLoading && <p className="muted">{copy("catalog.loading")}</p>}
      {q.isError && (
        <Empty
          title={copy("catalog.error")}
          body={copy(errorCopyKey(q.error))}
          action={
            <button type="button" className="cfg-btn" onClick={() => q.refetch()}>
              {copy("common.retry")}
            </button>
          }
        />
      )}
      {q.data && q.data.length === 0 && <Empty title={copy("catalog.empty")} body={copy("catalog.emptyBody")} />}
      {finder && q.data && q.data.length > 0 && (
        <h2 className="fit-results-title catalog-all-title" id="catalog-all">
          {copy("home.allTitle")}
        </h2>
      )}
      {groups && groups.length > 0 && (
        <div className="catalog-grid">
          {groups.map(({ design: d, members, variants }) => (
            <Card
              key={d.id}
              entry={{
                id: d.id,
                version: d.version,
                title: d.title,
                description: d.description,
                tags: d.tags,
                thumbnailUrl: d.thumbnailUrl,
                href: `/design/${encodeURIComponent(d.id)}`,
                kind: "design",
                downloads: downloads.data ? members.reduce((n, m) => n + (downloads.data[m.id] ?? 0), 0) : undefined,
                variants: variants.length ? variants : undefined,
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
